import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';


import { Box } from 'ui/box';
import { Stack } from 'ui/stack';

import ActionsDropdown from 'components/partials/actions-dropdown';
import type { ActionsDropdownOption } from 'components/partials/actions-dropdown/actions-dropdown';
import { Switch } from 'ui/switch';
import Tooltip from 'components/partials/tooltip/tooltip';

import RegimeRow from 'containers/admin/clients/client/tabs-components/aid-studio-tab/aid-studio-details/steps/matrix-step/components/regime-row';

import { getRegimeSortableId } from 'containers/admin/clients/client/tabs-components/aid-studio-tab/aid-studio-details/steps/matrix-step/hooks/use-regimes-sidebar-actions';

import Icons from 'assets/icons';

interface SortableRegimeItemProps {
  regime: GroupRegime;
  actions: ActionsDropdownOption[];
  isDragDisabled: boolean;
  readOnly?: boolean;
}

interface GroupRegime {
  id: string | number;
  name: string;
  sortOrder: number;
  groupId: string | number;
  pell: boolean;
}

interface GroupItem {
  id: string | number;
  name: string;
  enabled: boolean;
  regimes: GroupRegime[];
}

interface GroupRowProps {
  group: GroupItem;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onToggleEnabled: (checked: boolean) => void;
  groupActions: ActionsDropdownOption[];
  regimeActionsFactory: (regime: GroupRegime) => ActionsDropdownOption[];
  isToggleDisabled: boolean;
  isRegimeDragDisabled: (regimeId: string | number) => boolean;
  readOnly?: boolean;
}

const SortableRegimeItem = ({
  regime,
  actions,
  isDragDisabled,
  readOnly,
}: SortableRegimeItemProps): React.JSX.Element => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: getRegimeSortableId(regime.id),
    data: { type: 'regime', entityId: regime.id, groupId: regime.groupId },
    disabled: isDragDisabled,
  });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
  };

  return (
    <Box ref={setNodeRef} style={style} sx={[!!isDragging && { opacity: 0.3 }]}>
      <RegimeRow
        hideActions={readOnly}
        regime={regime}
        actions={actions}
        dragListeners={listeners as unknown as Record<string, unknown>}
        dragAttributes={attributes as unknown as Record<string, unknown>}
      />
    </Box>
  );
};

const GroupRow = ({
  group,
  isExpanded,
  onToggleExpand,
  onToggleEnabled,
  groupActions,
  regimeActionsFactory,
  isToggleDisabled,
  isRegimeDragDisabled,
  readOnly,
}: GroupRowProps): React.JSX.Element => {
  const groupNameRef = useRef<HTMLDivElement | null>(null);
  const [isGroupNameTruncated, setIsGroupNameTruncated] =
    useState<boolean>(false);

  const regimeIds = useMemo(
    () => (group.regimes || []).map((r) => getRegimeSortableId(r.id)),
    [group.regimes]
  );

  useLayoutEffect(() => {
    const element = groupNameRef.current;

    if (element) {
      setIsGroupNameTruncated(element.scrollWidth > element.clientWidth);
    }
  }, [group.name]);

  return (
    <Stack sx={{ gap: 0.5, pb: 0.5 }} data-testid={`group-row-${group.id}`}>
      <Stack
        direction="row"
        sx={{
          alignItems: 'center',
          gap: 1.5,
          p: 1,
          borderRadius: '0.5rem',
        }}
      >
        <Stack
          direction="row"
          sx={{
            flex: '1 1 0%',
            alignItems: 'center',
            gap: 1,
            pr: 1,
            minWidth: 0,
          }}
        >
          <Switch
            size="small"
            aria-label={`Enable ${group.name || 'group'}`}
            checked={group.enabled}
            onChange={(_event, checked) => onToggleEnabled(checked)}
            disabled={isToggleDisabled}
            data-testid={`group-toggle-${group.id}`}
          />

          <Tooltip
            content={group.name}
            enabled={isGroupNameTruncated}
            position="top-start"
          >
            <Box
              ref={groupNameRef}
              sx={{
                fontSize: '0.875rem',
                lineHeight: '1.25rem',
                fontWeight: 600,
                color: 'tokens.gray.700',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '9rem',
              }}
            >
              {group.name}
            </Box>
          </Tooltip>

          <button
            type="button"
            className="shrink-0 p-0.5"
            onClick={onToggleExpand}
            aria-label={isExpanded ? 'Collapse group' : 'Expand group'}
            data-testid={`group-expand-${group.id}`}
          >
            <Icons.ChevronRight
              customClass={classNames(
                'size-4 text-navy-500 transition-transform duration-200',
                { 'rotate-90': isExpanded }
              )}
            />
          </button>
        </Stack>

        {!readOnly && (
          <ActionsDropdown
            options={groupActions}
            variant="reserve-text"
            customClass="!h-auto !p-1"
          />
        )}
      </Stack>

      {isExpanded && group.regimes?.length > 0 && (
        <Stack sx={{ gap: 0.5 }}>
          <SortableContext
            items={regimeIds}
            strategy={verticalListSortingStrategy}
          >
            {group.regimes.map((regime) => (
              <SortableRegimeItem
                readOnly={readOnly}
                key={regime.id}
                regime={regime}
                actions={regimeActionsFactory(regime)}
                isDragDisabled={isRegimeDragDisabled(regime.id)}
              />
            ))}
          </SortableContext>
        </Stack>
      )}
    </Stack>
  );
};

export default GroupRow;
